
/**
 * Megamenu
 * Dropdown menu functionality made with accessibility in mind
 * Moves focus to the dropdown and back.
 *
 * The toggle matching `.navbar__mobile-menu-toggle` (the fullscreen mobile
 * menu button) is treated as the "root" toggle and gets extra modal-like
 * behaviour on top of the above: `<main>` is hidden from assistive tech
 * while it's open, and focus is kept from leaving the menu (both via Tab
 * and via assistive tech that moves focus directly, e.g. VoiceOver swipes).
 * The toggle itself doubles as the menu's visible close control, so it's
 * included as the trap boundary - otherwise Tab/swipe navigation could
 * never reach it and only Escape could close the menu.
 * See `rootToggle` and the `_*RootModalBehavior`/`_*RootFocus` methods.
 *
 * Markup example:
 * <nav>
 *  <ul>
 *    <li><button class="js-dropdown-toggle" data-dropdown-group="navbar" aria-controls="mega-palvelut">Palvelut<button></li>
 *    <li>...</li>
 *  </ul>
 *  <div class="megamenu__content-wrapper">
 *    <span class="js-back-to-opener" tabindex="-1"></span>
 *    <div class="megamenu__content" id="mega-palvelut">
 *    ...
 *    </div>
 *    <div class="megamenu__content" id="mega-tuotteet">
 *      ...
 *    </div>
 *    <span class="js-back-to-opener" tabindex="-1"></span>
 *  </div>
 * </nav>
 */
export default class MegaMenuDropdowns {

  /**
   * Constructor
   * @param {HTMLElement} container Area where the functionality is activated
   * @param {Object} config Configuration
   */
  constructor(container, config) {
    const defaults = {
      openOnHover: false,
      closeOnMouseOut: false,
      bodyClass: '' //class for body when dropdown is open
    };

    this.container = container;
    this.config = Object.assign({}, defaults, config);
    this.dropdownToggles = container.querySelectorAll('.js-dropdown-toggle'); // buttons that will open dropdown
    this.backTriggers = this.container.querySelectorAll('.js-back-to-opener'); //triggers that will return focus back to navigation
    this.closeButton = this.container.querySelector('.js-dropdown-closer');
    this.hoverTimer = null;
    this.activeToggle = null;
    //the toggle that opens the fullscreen mobile menu. Gets extra "modal-like"
    //behaviour (hides main content from assistive tech, traps Tab focus)
    this.rootToggle = this.container.querySelector('.navbar__mobile-menu-toggle');

    //binding 'this' here, to make adding/removing event listeners easier.
    this._onKeyPress = this._onKeyPress.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onRootTabKey = this._onRootTabKey.bind(this);
  }

  /**
   * Attach click event listeners to link area
   */
  init() {
    this.dropdownToggles.forEach((toggle) => {
      const toggleParent = toggle.parentElement;
      toggle.setAttribute('aria-expanded', false); //tells screenreaders that drodown is closed
      toggle.addEventListener('click', this._dropdownToggle.bind(this));

      if(this.config.openOnHover) {
        toggleParent.addEventListener('mouseover', this._onMouseOver.bind(this));
      }

      if(this.config.openOnHover && this.config.closeOnMouseOut) {
        toggleParent.addEventListener('mouseleave', this._onMouseLeave.bind(this));
      }
    });

    //if there is close button
    if(this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        if(this.activeToggle) {
          this.activeToggle.focus();
          this._closeDropdown(this.activeToggle);
        }
      });
    }

    //If user tabs outside the dropdown content, we will send the focus back to the dropdown toggle.
    //Exception: for the fullscreen mobile menu we loop focus back inside instead of closing it - this
    //also covers assistive tech that moves focus directly without a Tab keydown (e.g. VoiceOver swipe
    //navigation on iOS), which `_onRootTabKey` below can't intercept.
    [...this.backTriggers].forEach((backTrigger, index) => {
      backTrigger.addEventListener('focus', (event) => {
        if(this.activeToggle === this.rootToggle && this.rootToggle) {
          this._loopRootFocus(index === 0);
          return;
        }
        if(this.activeToggle) {
          this.activeToggle.focus();
          this._closeDropdown(this.activeToggle);
        }
      });
    });
  }

  /**
   * Add listeners that close dropdown
   */
  _addDocumentListeners() {
    //click outside area will close all dropdowns
    document.addEventListener('click', this._onDocumentClick);
    //Pressing esc will close all dropdowns
    document.addEventListener('keydown', this._onKeyPress);
  }

  /**
   * Remove listeners that close dropdown
   */
  _removeDocumentListeners() {
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('keydown', this._onKeyPress);
  }


  /**
   * Toggle dropdown
   * @param {Event} event Click event
   */
  _dropdownToggle(event) {
    const toggle = event.currentTarget;
    if(toggle.getAttribute('aria-expanded') === 'true') {
      this._closeDropdown(toggle);
    } else {
      this._openDropdown(toggle);
    }
  }

  /**
   * Open dropdown on mouse over
   * @param {Event} event mouseover event
   */
  _onMouseOver(event) {
    const toggle = event.currentTarget.querySelector('.js-dropdown-toggle');
    this._openDropdown(toggle);
  }

  /**
   * Close dropdown on mouse out
   * @param {Event} event mouseleave event
   */
  _onMouseLeave(event) {
    const toggle = event.currentTarget.querySelector('.js-dropdown-toggle');
    this.hoverTimer = setTimeout(() => this._closeDropdown(toggle));
  }

  /**
   * Checks if click event happaned outside the container
   * @param {Event} event click event
   */
  _onDocumentClick(event) {
    if(!this.container.contains(event.target)) {
      this._closeAllDropdowns();
    }
  }

  /**
   * Checks if pressed key was esc
   * @param {Event} event key press event
   */
  _onKeyPress(event) {
    if(event.key === 'Escape') {
      //if focus is inside menu area, move it to the active toggle
      const activeToggle = this.container.querySelector('.js-dropdown-toggle[aria-expanded="true"]');
      if(this.container.contains(document.activeElement) && activeToggle) {
        activeToggle.focus();
      }
      this._closeAllDropdowns();
    }
  }

  /**
   * Open dropdown
   * Adds class 'open' to the dropdown, set state for the toggle
   * @param {HTMLElement} toggle element that activated the dropdown
   */
  _openDropdown(toggle) {
    const dropdownGroup = toggle.dataset.dropdownGroup;
    if(dropdownGroup) {
      this._closeAllDropdowns(dropdownGroup);
    }

    clearTimeout(this.hoverTimer); //in case mouse-leave event is triggered, cancel it

    const dropdownId = toggle.getAttribute('aria-controls');
    //if dropdown is not directly under the toggle button, we can find it with aria-controls attribute
    const dropdown = dropdownId ? document.getElementById(dropdownId) : toggle.nextElementSibling;

    toggle.classList.add('selected');
    toggle.setAttribute('aria-expanded', true); //this tells screenreaders that dropdown is open

    dropdown.classList.add('open');
    setTimeout(() => {
      dropdown.classList.add('animated');
    }, 100);

    this._addDocumentListeners(); //adds listeners for ESC or clickin outside the area

    if(dropdownId) {
      this._makeElementFocusable(this.backTriggers);
      this._moveFocusToDropdown(dropdown, toggle);
    }

    if(this.config.bodyClass && toggle.dataset.bodyclass!=='false') {
      document.body.classList.add(this.config.bodyClass);
    }

    if(toggle === this.rootToggle) {
      this._openRootModalBehavior();
    }
  }

  /**
   * Close dropdown
   * Removes class 'open' from the dropdown, set state for the toggle
   * @param {HTMLElement} toggle - element that activated the dropdown
   */
  _closeDropdown(toggle) {
    const delay = toggle.dataset.animationDelay || 0;
    const dropdownId = toggle.getAttribute('aria-controls');
    //if dropdown is not directly under the toggle button, we can find it with aria-controls attribute
    const dropdown = dropdownId ? document.getElementById(dropdownId) : toggle.nextElementSibling;

    toggle.classList.remove('selected');
    toggle.setAttribute('aria-expanded', false);

    dropdown.classList.remove('animated');
    setTimeout(() => {
      dropdown.classList.remove('open');
    }, parseInt(delay));

    this._removeDocumentListeners();
    this.activeToggle = null;

    if(dropdownId) {
      this._makeElementUnfocusable(this.backTriggers);
    }

    //don't clear the body class if it's still needed for the root menu - e.g.
    //closing an accordion item inside the still-open root menu shouldn't tear
    //down state (like the .navbar positioning below) that the root menu owns.
    const rootStillOpen = toggle !== this.rootToggle && this.rootToggle &&
      this.rootToggle.getAttribute('aria-expanded') === 'true';
    if(this.config.bodyClass && !rootStillOpen) {
      document.body.classList.remove(this.config.bodyClass);
    }

    if(toggle === this.rootToggle) {
      this._closeRootModalBehavior();
    }
  }

  /**
  * Close dropdowns
  * @param {String} dropdownGroup - id defined, close only dropdowns that are in the same group
  */
  _closeAllDropdowns(dropdownGroup ='') {
    this.dropdownToggles.forEach((toggle) => {
      if(dropdownGroup && toggle.dataset.dropdownGroup !== dropdownGroup) {
        return;
      }
      if(toggle.getAttribute('aria-expanded') === 'true') {
        this._closeDropdown(toggle);
      }
    });
  }

  /**
   * Enable "modal-like" behaviour for the fullscreen mobile menu:
   * hide the page's main content from assistive tech (it's clipped out of
   * view behind the menu, but would otherwise stay reachable), and trap Tab
   * focus inside the menu so it can't leak into that hidden content.
   */
  _openRootModalBehavior() {
    const mainContent = document.querySelector('main');
    if(mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    }
    document.addEventListener('keydown', this._onRootTabKey);
  }

  /**
   * Undo `_openRootModalBehavior`
   */
  _closeRootModalBehavior() {
    const mainContent = document.querySelector('main');
    if(mainContent) {
      mainContent.removeAttribute('aria-hidden');
    }
    document.removeEventListener('keydown', this._onRootTabKey);
  }

  /**
   * Gets the element that `this.rootToggle` opens/controls.
   * @returns {HTMLElement|null}
   */
  _getRootDropdown() {
    if(!this.rootToggle) {
      return null;
    }
    const dropdownId = this.rootToggle.getAttribute('aria-controls');
    return dropdownId ? document.getElementById(dropdownId) : this.rootToggle.nextElementSibling;
  }

  /**
   * Keeps Tab focus inside the fullscreen mobile menu while it's open.
   * The toggle button doubles as the menu's visible close control once
   * expanded, so it's included as a trap stop - otherwise Tab/Shift+Tab
   * could never reach it and only Escape could close the menu.
   *
   * Every Tab press is driven explicitly (this function always calls
   * preventDefault and focuses the computed next/previous element itself),
   * rather than only handling the trap's boundaries and letting native Tab
   * order handle moves between the toggle and the dropdown, or between two
   * items inside it. Native focus changes can trigger the browser's own
   * scrollIntoView (e.g. landing on the language switcher near the end of
   * the list) - confirmed in the browser to nudge the real page scroll
   * position even though .navbar is meant to stay pinned on screen while
   * open, revealing page content behind/under the menu once that happens.
   * preventScroll avoids that, at the cost of not auto-scrolling a
   * genuinely offscreen item into view within the menu's own scroll area.
   *
   * Focusable elements are looked up fresh on every key press, since which
   * elements are visible changes as the user moves between menu levels.
   * @param {KeyboardEvent} event keydown event
   */
  _onRootTabKey(event) {
    if(event.key !== 'Tab') {
      return;
    }

    const dropdown = this._getRootDropdown();
    if(!dropdown) {
      return;
    }

    const focusable = [this.rootToggle, ...this._getFocusableElements(dropdown)];
    const currentIndex = focusable.indexOf(document.activeElement);
    const step = event.shiftKey ? -1 : 1;
    //if focus is somewhere the trap doesn't recognize, pull it back to the
    //matching end rather than stepping from an arbitrary starting point.
    const nextIndex = currentIndex === -1
      ? (event.shiftKey ? focusable.length - 1 : 0)
      : (currentIndex + step + focusable.length) % focusable.length;

    event.preventDefault();
    focusable[nextIndex].focus({preventScroll: true});
  }

  /**
   * Moves focus back inside the fullscreen mobile menu, used by the
   * `.js-back-to-opener` sentinels so focus loops instead of closing the
   * menu - this is what actually keeps focus contained for assistive tech
   * that moves focus directly (no Tab keydown for `_onRootTabKey` to catch).
   * Forward wraps to the toggle button (the menu's visible close control),
   * matching `_onRootTabKey`'s handling, so swipe navigation can reach it too.
   * @param {boolean} toLast focus the last focusable content element instead of the toggle
   */
  _loopRootFocus(toLast) {
    const dropdown = this._getRootDropdown();
    if(!dropdown) {
      return;
    }
    if(!toLast) {
      this.rootToggle.focus({preventScroll: true});
      return;
    }
    const focusable = this._getFocusableElements(dropdown);
    const target = focusable.length ? focusable[focusable.length - 1] : this.rootToggle;
    target.focus({preventScroll: true});
  }

  /**
   * Gets all keyboard-focusable elements within a specified element, in DOM order.
   * Excludes the internal `.js-back-to-opener` sentinels used elsewhere in this file.
   *
   * Menu levels the user hasn't drilled into are still in the DOM, just hidden
   * via `display: none` on an *ancestor* (`.megamenu__panel`) - `getComputedStyle`
   * only reflects an element's own `display` value, not its ancestors', so it
   * can't be used to detect that. `offsetParent` is null for anything hidden by
   * itself or an ancestor, which is what we actually need here.
   * @param {HTMLElement} container element to search within
   * @returns {HTMLElement[]}
   */
  _getFocusableElements(container) {
    //`summary`, not `details` - the browser only ever puts the <summary>
    //child in the tab order, never the wrapping <details> element itself
    const elements = container.querySelectorAll(
      'a[href], button, input, textarea, select, summary, [tabindex]:not([tabindex="-1"])'
    );
    return [...elements].filter((el) => {
      if(el.classList.contains('js-back-to-opener') || el.hasAttribute('disabled')) {
        return false;
      }
      //a closed <details>'s own content can still pass the offsetParent check
      //below - e.g. the language switcher lays its content out with
      //display:flex to work around <details> not supporting flexbox well,
      //which can defeat the browser's native "hide content while closed"
      //behaviour. Explicitly skip anything but the summary of a closed one.
      const detailsAncestor = el.closest('details');
      if(detailsAncestor && !detailsAncestor.open && el.tagName !== 'SUMMARY') {
        return false;
      }
      return el.offsetParent !== null;
    });
  }

  /**
   * Moves focus to dropdown
   * @param {HTMLElement} dropdown element that will receive focus
   * @param {HTMLElement} toggle button that triggered this action
   */
  _moveFocusToDropdown(dropdown, toggle) {
    this.activeToggle = toggle; //store active toggle
    //Move focus to itself or to the first focusable element inside
    const isSelfFocusable = dropdown.getAttribute( 'tabindex' ) === '-1' || dropdown.getAttribute( 'tabindex' ) === '0';
    const focusable = isSelfFocusable ? dropdown : this._getKeyboardFocusableElement(dropdown);
    if(focusable) {
      focusable.focus();
    }
  }

  /**
  * Gets keyboard-focusable elements within a specified element
  * @param {HTMLElement} element element
  * @returns {HTMLElement}
  */
  _getKeyboardFocusableElement(element) {
    //`summary`, not `details` - see _getFocusableElements above
    return [...element.querySelectorAll(
      'a, button, input, textarea, select, summary,[tabindex]:not([tabindex="-1"])'
    )].find(el => !el.hasAttribute('disabled') && el.offsetParent !== null );
  }

  /**
   * Make element/elements focusable by adding tabindex=0
   * @param {HTMLElement | NodeList} element element/elements picked with queryslector/queryselectorAll
   */
  _makeElementFocusable(element){
    if(element.length){
      element.forEach((singleElement) => {
        singleElement.setAttribute('tabindex', 0);
      });
    } else if(element){
      element.setAttribute('tabindex', 0);
    }
  }

  /**
   * Make element unfocusable by adding tabindex="-1"
   * @param {HTMLElement | NodeList} element element/elements picked with queryslector/queryselectorAll
   */
  _makeElementUnfocusable(element){
    if(element.length){
      element.forEach((singleElement) => {
        singleElement.setAttribute('tabindex', '-1');
      });
    } else if(element){
      element.setAttribute('tabindex', '-1');
    }
  }

}
